import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import FormModal from '../common/form/FormModal';
import { FormProvider } from '../common/form/FormContext';
import { cloneDeep } from 'lodash';
import { Reference } from 'sr-types/lib/person/v1/graphql';
import { QuickAddOrganizationForm } from './QuickAddOrganizationForm';
import {
    client,
    EMPTY_STATE,
    inputEntityForQuickAddOrg,
    organizationLineItemSave,
    showLocationForQuickAddOrg,
    validationRulesForQuickAddOrg,
    saveQuery
} from './organization';
import { LineItemInput } from 'sr-types/lib/shortlist/v1/graphql';
import { useMutationWithContext } from '../common/hooks/useMutationWithContext';
import { useI18n } from '../common/i18n/I18n';
import { Resource } from 'sr-types/lib/production/v1/graphql';

const EMPTY_SHORTLIST_LINE_ITEM: LineItemInput = {
    name: undefined,
    itemReference: undefined,
    itemStatus: undefined,
    summary: { about: undefined, headline: undefined },
    defaultNoteReference: {
        id: undefined,
        label: undefined
    }
};

export default ({
    organizationModalState,
    setOrganizationModalState,
    id,
    onAddToShortlist,
    resourceData,
    setSearch,
    addOrgPublicName = undefined,
    handleOnCloseAddOrgModal
}: {
    organizationModalState: boolean;
    setOrganizationModalState: Dispatch<SetStateAction<boolean>>;
    id: string;
    onAddToShortlist?: (data: { name: string; reference: Reference }) => void;
    resourceData?: Resource;
    setSearch?: Dispatch<SetStateAction<string>>;
    addOrgPublicName?: string;
    handleOnCloseAddOrgModal?: () => void;
}) => {
    const [saveDisabled, setIsSaveDisabled] = useState(false);
    const [lineItemValues, setLineItemValues] = useState(cloneDeep(EMPTY_SHORTLIST_LINE_ITEM));
    const [isSaving, setIsSaving] = useState(false);
    const [save] = useMutationWithContext(saveQuery, client);
    const [organizationState, setOrganizationState] = useState<any>({ ...EMPTY_STATE, publicName: addOrgPublicName });

    const onClose = () => {
        setOrganizationModalState(false);
        setLineItemValues(cloneDeep(EMPTY_SHORTLIST_LINE_ITEM));
        handleOnCloseAddOrgModal?.();
    };

    const checkShowLocation = () => {
        return showLocationForQuickAddOrg(organizationState, resourceData);
    };
    const validationRulesForOrgState = useMemo(() => {
        return validationRulesForQuickAddOrg(organizationState, resourceData);
    }, [organizationState.organizationTypes]);

    const saveLineItemToMarketplace = () => {
        setIsSaving(true);
        const savedEntity = inputEntityForQuickAddOrg(organizationState, resourceData);

        organizationLineItemSave(
            savedEntity,
            save,
            setIsSaving,
            setOrganizationModalState,
            lineItemValues,
            setLineItemValues,
            onAddToShortlist
        );
        handleOnCloseAddOrgModal?.();
        setSearch?.(savedEntity?.publicName);
        setOrganizationModalState(false);
        setOrganizationState({ ...EMPTY_STATE });
    };

    return (
        organizationModalState && (
            <FormProvider
                validationRules={validationRulesForOrgState}
                state={organizationState}
                setState={setOrganizationState}
            >
                <FormModal
                    id={'shortlist-line-items'}
                    title={useI18n('form.organization.add')}
                    onClose={onClose}
                    isSaving={isSaving}
                    isOpen={organizationModalState}
                    isSaveDisabled={saveDisabled}
                    onSave={saveLineItemToMarketplace}
                >
                    <QuickAddOrganizationForm
                        id={id}
                        setIsSaveDisabled={setIsSaveDisabled}
                        checkShowLocation={checkShowLocation}
                    />
                </FormModal>
            </FormProvider>
        )
    );
};
